import { shallowEqual } from "react-redux";

import deepEqual from "fast-deep-equal";
import memoize from "fast-memoize";
import { uniq } from "lodash-es";
import { createSelector, lruMemoize } from "reselect";

import { getUserCumulRole } from "@mapmycustomers/shared";
import { OrganizationSetting } from "@mapmycustomers/shared";
import BoundaryLegendCreationVisibility from "@mapmycustomers/shared/enum/boundaryLegends/BoundaryLegendCreationVisibility";
import CumulAddOnServiceVariation from "@mapmycustomers/shared/enum/cumul/CumulAddOnServiceVariation";
import EntityType from "@mapmycustomers/shared/enum/EntityType";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import IntegrationService from "@mapmycustomers/shared/enum/integrations/IntegrationService";
import LeadFinderSubscriptionPlan, {
  LEAD_FINDER_ENABLED_PLANS,
} from "@mapmycustomers/shared/enum/LeadFinderSubscriptionPlan";
import BaseMapStyle from "@mapmycustomers/shared/enum/map/BaseMapStyle";
import MarkerIcon from "@mapmycustomers/shared/enum/MarkerIcon";
import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import MileageTrackingVisibility from "@mapmycustomers/shared/enum/mileageTracking/MileageTrackingVisibility";
import OrganizationFeature from "@mapmycustomers/shared/enum/OrganizationFeature";
import PermissionName from "@mapmycustomers/shared/enum/PermissionName";
import PinLegendCreationVisibility from "@mapmycustomers/shared/enum/pinLegends/PinLegendCreationVisibility";
import RoleEnum from "@mapmycustomers/shared/enum/Role";
import TerritoryAddOnServiceVariation from "@mapmycustomers/shared/enum/TerritoryAddOnServiceVariation";
import Version from "@mapmycustomers/shared/enum/Version";
import HasSourceObject from "@mapmycustomers/shared/types/base/HasSourceObject";
import { EntityTypesSupportingCustomSourceObject } from "@mapmycustomers/shared/types/entity";
import { DuplicateSearchFilter } from "@mapmycustomers/shared/types/entity/Duplicate";
import { OrganizationMetaData } from "@mapmycustomers/shared/types/Organization";
import PersistentFieldAgent from "@mapmycustomers/shared/types/persistent/PersistentFieldAgent";
import PersistentFieldInsights from "@mapmycustomers/shared/types/persistent/PersistentFieldInsights";
import Role from "@mapmycustomers/shared/types/Role";
import Setting from "@mapmycustomers/shared/types/Setting";
import BulkEmailSettingValue from "@mapmycustomers/shared/types/settingValues/BulkEmailSettingValue";
import CalendarSyncPreferencesValue from "@mapmycustomers/shared/types/settingValues/CalendarSyncPreferencesValue";
import UserTeamAccessValue from "@mapmycustomers/shared/types/settingValues/UserTeamAccessValue";
import SourceObjectValue from "@mapmycustomers/shared/types/SourceObjectValue";
import User, { UserRef } from "@mapmycustomers/shared/types/User";
import {
  isDefined,
  isEntityTypeSupportingCustomSourceObject,
} from "@mapmycustomers/shared/util/assert";

import localSettings from "@app/config/LocalSettings";
import CheckInVerifiedVisibility from "@app/enum/CheckInVerifiedVisibility";
import CustomStackRankMetricsCreationSettingValue from "@app/enum/CustomStackRankMetricsCreationSettingValue";
import DuplicateMergeAccessValue from "@app/enum/DuplicateMergeAccessValue";
import UserSetting from "@app/enum/UserSetting";
import EmailSettings from "@app/scene/settings/type/EmailSettings";
import { getActivityTypes } from "@app/store/activity";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import FieldAgentInstructionSettings from "@app/types/FieldAgentInstructionSettings";
import PermissionGetter from "@app/types/PermissionGetter";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { getIntegrationServiceName } from "@app/util/integrations/integrationServiceNames";
import { hasCustomSourceObjects } from "@app/util/ui";

import ImportRestrictionValue from "../../enum/ImportRestrictionValue";

import { isOwner as isOwnerPredicate } from "./util";

const iamState = (state: RootState) => state.iam;

export const isLoading = createSelector(iamState, ({ loading }) => loading);
export const getError = createSelector(iamState, ({ error }) => error);

export const getMe = createSelector(iamState, ({ me }) => me);

// returns only current user's details, without org info and other fields
export const getCurrentUser = createSelector(
  getMe,
  (me) => {
    if (!me) {
      return me;
    }

    const {
      accessRights,
      layouts,
      organization,
      settings,
      signature,
      ssoLogin,
      usageLimits,
      ...myUser
    } = me;

    return myUser;
  },
  {
    memoize: lruMemoize,
    memoizeOptions: {
      equalityCheck: shallowEqual,
      maxSize: 1,
      resultEqualityCheck: deepEqual,
    },
  }
);

export const getCurrentUserId = createSelector(getMe, (me) => me?.id);
export const getUserMetaData = createSelector(getMe, (me) => me?.metaData ?? {});
export const getFieldAgentMetaData = createSelector(
  getUserMetaData,
  (metaData): PersistentFieldAgent => metaData.fieldAgent ?? {}
);
export const getFieldInsightsMetaData = createSelector(
  getUserMetaData,
  (metaData): PersistentFieldInsights => metaData.fieldInsights ?? {}
);

export const getRole = createSelector(getMe, (me) => me?.role);
export const isCurrentUserOwner = createSelector(getRole, (role) => role?.key === RoleEnum.OWNER);
export const isCurrentUserManager = createSelector(
  getRole,
  (role) => role?.key === RoleEnum.MANAGER
);
export const isCurrentUserMember = createSelector(getRole, (role) => role?.key === RoleEnum.MEMBER);
export const isCurrentUserReadOnly = createSelector(
  getRole,
  (role) => role?.key === RoleEnum.READ_ONLY || role?.key === RoleEnum.READ_ONLY_WITH_EXPORTS
);

export const getUserPermissions = createSelector(iamState, (iam) => iam.permissionMap);

export const hasPermission = createSelector(getUserPermissions, (permissions) =>
  memoize<PermissionGetter>(
    (permissionName: PermissionName): boolean => permissions[permissionName] ?? false
  )
);

// Mirrors the platform's gate: anyone else gets the field omitted, so the report would look empty.
export const canReadDevicePermissions = createSelector(
  isCurrentUserManager,
  isCurrentUserOwner,
  hasPermission,
  (isManager, isOwner, hasPermission) =>
    isManager || isOwner || hasPermission(PermissionName.DEVICE_PERMISSIONS_READ)
);

export const getUserSettings = createSelector(getMe, (me) => me?.settings);

export const getUserSetting = createSelector(
  [getUserSettings, (_, settingName: string) => settingName],
  (settings, settingName) => settings?.find(({ key }) => key === settingName)
);

export const getUserSettingValue = createSelector(
  [getUserSettings, (_, settingName: string) => settingName],
  (settings, settingName) => {
    const setting = settings?.find(({ key }) => key === settingName);
    return setting?.value;
  }
);

export const isSettingUpdating = createSelector(iamState, ({ settingUpdating }) => settingUpdating);

export const getBulkEmailSetting = createSelector(
  (state) => getUserSetting(state, UserSetting.BULK_EMAIL),
  (bulkEmailSetting) => {
    if (!bulkEmailSetting) {
      return undefined;
    }

    const defaultValue: BulkEmailSettingValue = {
      endTime: "18:00",
      excludedDays: [],
      signature: false,
      signatureText: "",
      startTime: "08:00",
      // Matches the server's seeded default. Defaulting to false hid the
      // open/click chips for users whose stored value predates this key.
      trackEmails: true,
    };

    const result: Setting<BulkEmailSettingValue> = {
      ...bulkEmailSetting,
      value: {
        ...defaultValue,
        ...bulkEmailSetting.value,
      },
    };

    return result;
  }
);

// "Show opens and clicks" is a display preference, and the server seeds it on, so
// only an explicit false may hide the chips. Every "we have no answer" state — no
// settings loaded, no bulkEmail row in this org, or a row predating the key — reads
// as on. Owning that here keeps the four chip call sites from each re-deriving it.
export const areEmailMetricsVisible = createSelector(
  getBulkEmailSetting,
  (bulkEmailSetting) => bulkEmailSetting?.value?.trackEmails ?? true
);

// collect map settings from Iam instance
// currently reads view settings from user metadata and userSettings (in this order)
export const getLegacyMapSettings = createSelector(
  [
    getMe,
    (state) => getUserSettingValue(state, "markerIcon"),
    (state) => getUserSettingValue(state, "markerSize"),
  ],
  (me, markerIcon, markerSize) => {
    const metadata = me?.metaData;
    return {
      markerIcon: metadata?.markerIcon ?? markerIcon ?? MarkerIcon.PIN,
      markerSize: metadata?.markerSize ?? markerSize ?? MarkerSize.LARGE,
    };
  }
);

// collect modern map settings from Iam instance
// currently reads view settings from user metadata and userSettings (in this order)
export const getMapSettings = createSelector(
  [getMe, (state) => getUserSettingValue(state, "markerSize")],
  (me, markerSize) => {
    const metadata = me?.metaData;
    return {
      markerSize: metadata?.markerSize ?? markerSize ?? MarkerSize.LARGE,
      showEntityIcons: metadata?.showEntityIcons ?? true,
      style: metadata?.mapSettings?.style ?? BaseMapStyle.STANDARD,
    };
  }
);

export const getPinnedDashboardIds = createSelector(
  getMe,
  (me) => me?.metaData?.pinnedDashboardIds ?? []
);

export const getPinnedResourceIds = createSelector(
  getMe,
  (me) => me?.metaData?.pinnedResourceIds ?? []
);

export const getFavoriteSavedFilterIds = createSelector(
  getUserMetaData,
  (metaData) => metaData.quickFilters ?? []
);

export const getDefaultMapFilterId = createSelector(
  getUserMetaData,
  (metaData) => metaData.defaultMapFilterId ?? undefined
);

export const getHiddenTerritoriesIds = createSelector(
  getUserMetaData,
  (metaData) => metaData.hiddenTerritoriesIds ?? [],
  {
    memoize: lruMemoize,
    memoizeOptions: {
      equalityCheck: shallowEqual,
      maxSize: 10,
      resultEqualityCheck: deepEqual,
    },
  }
);
export const isCumulPromotionalUIVisited = createSelector(
  getUserMetaData,
  (metaData) => metaData.isCumulDemoVisited
);

export const getPhiPromotionAlertDismissed = createSelector(
  getUserMetaData,
  (metaData) => metaData.phiPromotionBannerDismissed
);

export const getTeamDashboardAlertDismissedAt = createSelector(
  getUserMetaData,
  (metaData) => metaData.teamDashboardAlertDismissedAt
);

export const getTimelineEventsExpanded = createSelector(
  getUserMetaData,
  (metaData) => metaData.timelineEventsExpanded ?? false
);

export const getOrganization = createSelector(getMe, (me) => me?.organization);

// Only the CRMs the mention emails support carry a name.
export const getIntegrationNameIfIntegrationOnlyGetter = createSelector(
  getOrganization,
  (organization): ((user: UserRef) => string | undefined) | undefined => {
    const crmName = (organization?.integrationTypes ?? [])
      .map(getIntegrationServiceName)
      .find(isDefined);
    const userIds = new Set(organization?.integrationOnlyUserIds ?? []);
    if (!crmName || !userIds.size) {
      return undefined;
    }
    return (user) => (userIds.has(user.id) ? crmName : undefined);
  }
);

export const getOrganizationId = createSelector(
  getOrganization,
  (organization) => organization?.id
);

export const getOrganizationName = createSelector(
  getOrganization,
  (organization) => organization?.name
);
export const getOrganizationSettings = createSelector(
  getOrganization,
  (organization) => organization?.settings
);
export const getOrganizationSetting = createSelector(
  [getOrganizationSettings, (_, settingName: OrganizationSetting) => settingName],
  (settings, settingName) => settings?.find(({ key }) => key === settingName)
);

export const getOrganizationSettingValue = createSelector(
  [getOrganizationSettings, (_, settingName: OrganizationSetting) => settingName],
  (settings: Setting[] | undefined, settingName: OrganizationSetting) => {
    const setting = settings?.find(({ key }) => key === settingName);
    return setting?.value;
  }
);

export const getCalendarSyncPreferences = createSelector(
  (state: RootState) =>
    getOrganizationSettingValue(state, OrganizationSetting.CALENDAR_SYNC_PREFERENCES),
  (value) => value as CalendarSyncPreferencesValue | undefined
);

export const shouldIncludeSharedActivities = createSelector(
  getOrganizationSettings,
  (settings) =>
    settings?.find(({ key }) => key === OrganizationSetting.CROSS_OBJECT_ACTIVITY_VISIBILITY)
      ?.value ?? true
);

// Without both rows a save would report success and store nothing, so the tab hides instead.
export const getFieldAgentInstructionSettings = createSelector(
  getOrganizationSettings,
  (settings): FieldAgentInstructionSettings | undefined => {
    const brief = settings?.find(
      ({ key }) => key === OrganizationSetting.FIELD_AGENT_BRIEF_INSTRUCTION
    );
    const recap = settings?.find(
      ({ key }) => key === OrganizationSetting.FIELD_AGENT_RECAP_INSTRUCTION
    );
    return brief && recap ? { brief, recap } : undefined;
  }
);

export const getOrganizationOwnerId = createSelector(
  getOrganization,
  (organization) => organization?.owner.id
);

export const getOrganizationAddOnServices = createSelector(
  getOrganization,
  (organization) => organization?.addOnServices
);

export const getUserAddOnServices = createSelector(getCurrentUser, (user) => user?.addOns);

export const doesOrganizationSupportDupeCheck = createSelector(
  getOrganizationAddOnServices,
  (addOnServices) => !!addOnServices?.dupeCheck
);

export const isFieldViewEnabled = createSelector(
  getOrganizationAddOnServices,
  (addOnServices) => !!addOnServices?.fieldView
);

export const doesOrganizationSupportMultiContactActivities = createSelector(
  getOrganizationAddOnServices,
  (addOnServices) => !!addOnServices?.multiContactActivities
);

export const doesOrganizationSupportSavedLocations = createSelector(
  getOrganizationAddOnServices,
  (addOnServices) => !!addOnServices?.savedLocations
);

export const isZohoOrganization = createSelector(
  getOrganization,
  (organization) => !!organization?.integrationTypes?.includes(IntegrationService.ZOHO)
);

export const doesOrganizationSupportEfficiencyReporting = createSelector(
  getOrganizationAddOnServices,
  (addOnServices) => !!addOnServices?.efficiencyReporting
);

// Field Performance Package promos are only shown to owners of orgs without the package —
// they are the only role that can act on an upgrade.
export const isEligibleForFieldPerformancePromo = createSelector(
  doesOrganizationSupportEfficiencyReporting,
  isCurrentUserOwner,
  (isEfficiencyReportingEnabled, isOwner) => !isEfficiencyReportingEnabled && isOwner
);

export const doesOrganizationSupportMileageTracking = createSelector(
  getOrganizationAddOnServices,
  (addOnServices) => !!addOnServices?.mileageTracking
);

// Whether the viewer can see mileage data: the org add-on plus their own seat or permission. For
// anything that states a fact about the organization — explanatory copy, upsells — use
// doesOrganizationSupportMileageTracking instead, or a seatless member gets told their org lacks an
// add-on it actually pays for.
export const isMileageTrackingVisible = createSelector(
  doesOrganizationSupportMileageTracking,
  getUserAddOnServices,
  hasPermission,
  (isSupportedByOrganization, userAddOns, hasPermission) =>
    isSupportedByOrganization &&
    (!!userAddOns?.mileageTracking || hasPermission(PermissionName.TRIPS_READ))
);

// Org-only, unlike doesUserHaveAccessToAiFeatures: an Owner configuring the org keeps the tab
// even when their own seat lacks the add-on.
export const doesOrganizationSupportAiFeatures = createSelector(
  getOrganizationAddOnServices,
  (addOnServices) => !!addOnServices?.aiFeatures
);

// Both halves are required because the platform gates on both: a rep in an entitled org whose
// own seat lacks the add-on still gets a 403 from the chat endpoint.
export const doesUserHaveAccessToAiFeatures = createSelector(
  getOrganizationAddOnServices,
  getUserAddOnServices,
  (orgAddOns, userAddOns) => !!orgAddOns?.aiFeatures && !!userAddOns?.aiFeatures
);

export const getAllowedStorageForResources = createSelector(
  getOrganizationAddOnServices,
  (orgAddOns) => (orgAddOns?.resourceStorage ?? 0) + (orgAddOns?.purchasedStorage ?? 0)
);

export const getMileageTrackingAmountsVisibility = (
  visibility: MileageTrackingVisibility,
  role?: Role
) => {
  switch (visibility) {
    case MileageTrackingVisibility.ALL:
      return true;
    case MileageTrackingVisibility.OWNER:
      return role?.key === RoleEnum.OWNER;
    case MileageTrackingVisibility.TEAM:
      return role?.key === RoleEnum.OWNER || role?.key === RoleEnum.MANAGER;
    default:
      return false;
  }
};

export const areMileageTrackingAmountsVisible = createSelector(
  [
    getRole,
    (state) => getOrganizationSettingValue(state, OrganizationSetting.MILEAGE_TRACKING_VISIBILITY),
  ],
  (role, visibility) => {
    return getMileageTrackingAmountsVisibility(visibility, role);
  }
);

export const doesOrganizationSupportTerritories = createSelector(
  getOrganizationAddOnServices,
  (addOnServices) =>
    !!addOnServices?.territories && addOnServices.territories !== TerritoryAddOnServiceVariation.OFF
);

export const doesOrganizationSupportSmartTerritories = createSelector(
  getOrganizationAddOnServices,
  (addOnServices) => addOnServices?.territories === TerritoryAddOnServiceVariation.FULL
);

export const doesOrganizationSupportAdvancedRouting = createSelector(
  getOrganizationAddOnServices,
  (addOnServices) => !!addOnServices?.advancedRouting
);

export const doesOrganizationSupportHipaa = createSelector(
  getOrganizationAddOnServices,
  (addOnServices) => !!addOnServices?.hipaaEnabled
);

export const doesOrganizationSupportDemographicData = createSelector(
  getOrganizationAddOnServices,
  (addOnServices) => !!addOnServices?.demographicData
);

export const getLeadGenPlan = createSelector(
  getOrganizationAddOnServices,
  (addOnServices) => addOnServices?.leadGen
);

export const isOnboardingEnabled = createSelector(
  getOrganizationAddOnServices,
  (orgAddOns) => !!orgAddOns?.userGuides
);

export const isResourcesEnabled = createSelector(
  getOrganizationAddOnServices,
  (orgAddOns) => !!orgAddOns?.pinnedResource
);

export const isCoachingAndInsightsEnabled = createSelector(
  getOrganizationAddOnServices,
  (orgAddOns) => !!orgAddOns?.coachingAndInsights
);

// Effective gate for the ExecHQ feature: the add-on entitlement plus the
// org-level master toggle. The add-on alone only unlocks the org setting; the feature
// stays off until an owner enables it.
export const isCoachingAndInsightsActive = createSelector(
  isCoachingAndInsightsEnabled,
  getOrganizationSettings,
  (addOnEnabled, settings) =>
    addOnEnabled &&
    !!settings?.find(({ key }) => key === OrganizationSetting.COACHING_AND_INSIGHTS)?.value
);

// Whether the logged-in user may use ExecHQ: the feature is active and the
// access list grants it to them (all users, their id, or one of their teams). Access is
// resolved per viewer — an enabled user sees every scope's tabs, a disabled one sees none.
export const hasCoachingAndInsightsAccess = createSelector(
  isCoachingAndInsightsActive,
  getOrganizationSettings,
  getCurrentUser,
  (active, settings, user) => {
    if (!active || !user) {
      return false;
    }
    const access: undefined | UserTeamAccessValue = settings?.find(
      ({ key }) => key === OrganizationSetting.COACHING_AND_INSIGHTS_ACCESS
    )?.value;
    if (!access) {
      return false;
    }
    return (
      access.allUsers ||
      !!access.userIds?.includes(user.id) ||
      user.teams.some(({ id }) => access.teamIds?.includes(id))
    );
  }
);

export const isLeadFinderEnabled = createSelector(
  getLeadGenPlan,
  (plan) => !!plan && LEAD_FINDER_ENABLED_PLANS.includes(plan)
);

export const isLeadFinderBasic = createSelector(
  getLeadGenPlan,
  (plan) => plan === LeadFinderSubscriptionPlan.BASIC
);

export const isLeadFinderSaveEnabled = createSelector(
  isLeadFinderEnabled,
  isLeadFinderBasic,
  (isLfEnabled, isBasic) => isLfEnabled && !isBasic
);

export const isSsoEnabled = createSelector(
  getOrganization,
  (organization) => !!organization?.domain
);

export const getPlan = createSelector(getOrganization, (organization) => organization?.plan);

export const getColors = createSelector(
  getOrganization,
  (organization) => organization?.plan.colors
);

export const getPosition = createSelector(iamState, ({ position }) => position);
export const getUserCountry = createSelector(iamState, ({ userCountry }) => userCountry);

//Change Password Selectors
export const isChangePasswordLoading = createSelector(
  iamState,
  ({ changePasswordLoading }) => changePasswordLoading
);
export const getChangePasswordError = createSelector(
  iamState,
  ({ changePasswordError }) => changePasswordError
);

export const isChangePasswordModalVisible = createSelector(
  iamState,
  ({ changePasswordModalVisible }) => changePasswordModalVisible
);
// Edit Profile Photo Selectors
export const isConfirmDeleteProfilePhotoModalVisible = createSelector(
  iamState,
  ({ confirmDeleteProfileModalVisible }) => confirmDeleteProfileModalVisible
);
export const isDeleteProfilePhotoLoading = createSelector(
  iamState,
  ({ deleteProfilePhotoLoading }) => deleteProfilePhotoLoading
);

export const isUploadProfilePhotoLoading = createSelector(
  iamState,
  ({ uploadProfilePhotoLoading }) => uploadProfilePhotoLoading
);
//onboarding

export const isFinishingOnboarding = createSelector(
  iamState,
  ({ finishOnboardingLoading }) => finishOnboardingLoading
);

export const isOnTrialPlan = createSelector(getOrganization, (org) => org?.plan.trialing);

export const getOrganizationMetaData = createSelector(
  getOrganization,
  (org) => org?.metaData ?? {}
);

// stable reference so useSelector does not re-render
const NO_SOURCE_OBJECTS: SourceObjectValue[] = [];

export const getSourceObjectValues = (
  state: RootState,
  entityType: EntityType | undefined
): SourceObjectValue[] =>
  isEntityTypeSupportingCustomSourceObject(entityType)
    ? (getOrganization(state)?.sourceObjects?.[entityType]?.values ?? NO_SOURCE_OBJECTS)
    : NO_SOURCE_OBJECTS;

export const doesOrganizationUseSourceObjects = (
  state: RootState,
  entityType: EntityType | undefined
): boolean => hasCustomSourceObjects(getSourceObjectValues(state, entityType));

// null is the standard object and a legal default, so a missing default and a standard default
// are the same answer here
export const getDefaultSourceObjectKey = (
  state: RootState,
  entityType: EntityType | undefined
): HasSourceObject["sourceObject"] =>
  getSourceObjectValues(state, entityType).find(({ isDefault }) => isDefault)?.key ?? null;

// May variants be created for this entity type?
// Deals and Activities are always allowed
export const canCreateLayoutVariants = (
  state: RootState,
  entityType: EntityType | undefined
): boolean =>
  !isEntityTypeSupportingCustomSourceObject(entityType) ||
  doesOrganizationUseSourceObjects(state, entityType);

// The server stamps the org default on new records. Gated, since filtering on null for an org
// without source objects would hide records still stamped by a source it has since removed.
export const getDuplicateCheckSourceObjectFilter = (
  state: RootState,
  entityType: EntityTypesSupportingCustomSourceObject
): Pick<DuplicateSearchFilter, "sourceObject"> =>
  doesOrganizationUseSourceObjects(state, entityType)
    ? { sourceObject: { $eq: getDefaultSourceObjectKey(state, entityType) } }
    : {};

// assuming all users are on v2-beta already, so we can use it as a default one even when webVersion is not
// explicitly set for user.
const DEFAULT_VERSION = Version.V2_BETA;

export const getVersion = createSelector(
  getOrganizationMetaData,
  (metaData) => metaData?.webVersion ?? DEFAULT_VERSION
);
const DEFAULT_FEATURES: OrganizationMetaData["features"] = {
  [OrganizationFeature.DISALLOW_COLOR]: { enabled: false },
};

export const getFeatures = createSelector(
  getOrganizationMetaData,
  (metaData) => metaData?.features ?? DEFAULT_FEATURES
);

export const getDefaultColumns = createSelector(getOrganizationMetaData, (metaData) => {
  const defaultColumns = { ...metaData?.defaultColumns };
  ([EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL, EntityType.ACTIVITY] as const).forEach(
    (entityType) => {
      const fieldModel = getFieldModelByEntityType(entityType);

      // if present in defaultColumns, use that value, naming removed fields by their replacements...
      if (entityType in defaultColumns) {
        defaultColumns[entityType] = uniq(
          defaultColumns[entityType]?.map((platformName) => {
            const field = fieldModel?.getByPlatformName(platformName);
            // keep names listed in the field's platformFilterNames; rename legacy aliases
            return field && !field.platformFilterNames.includes(platformName)
              ? field.platformName
              : platformName;
          })
        );
        return;
      }

      if (!fieldModel) {
        return; // shouldn't really happen, but...
      }

      // ...otherwise, using a system default columns
      defaultColumns[entityType] = fieldModel.fields
        .filter((field) =>
          field.hasAnyFeature(FieldFeature.VISIBLE_BY_DEFAULT, FieldFeature.ALWAYS_VISIBLE)
        )
        .map((field) => field.platformName);
    }
  );
  return defaultColumns;
});

export const getPinLegendCreationVisibility = createSelector(
  getOrganizationMetaData,
  (metaData) => metaData?.pinLegendsCreation ?? PinLegendCreationVisibility.PRIVATE
);

export const getBoundaryLegendCreationVisibility = createSelector(
  getOrganizationMetaData,
  (metaData) => metaData?.boundaryColorCreation ?? BoundaryLegendCreationVisibility.PRIVATE
);

export const getDefaultColorKeys = createSelector(
  getOrganizationMetaData,
  (metaData) => metaData?.defaultColorKey
);

export const getDefaultShapeKeys = createSelector(
  getOrganizationMetaData,
  (metaData) => metaData?.defaultShapeKey
);

export const areCheckInFieldsVisible = createSelector(
  getRole,
  getOrganizationSettings,
  (role: Role | undefined, orgSettings: Setting[] | undefined) => {
    const checkInVisibility =
      orgSettings?.find(({ key }) => key === OrganizationSetting.CHECK_IN_VERIFIED_VISIBILITY)
        ?.value ?? CheckInVerifiedVisibility.OWNER;

    if (role?.key === RoleEnum.OWNER) {
      return true; // owners have access to everything
    }
    if (role?.key === RoleEnum.MANAGER) {
      // managers have access to everything too except when visibility is set to OWNER
      return checkInVisibility !== CheckInVerifiedVisibility.OWNER;
    }
    // for other users it is only accessible if visibility is set to public
    return checkInVisibility === CheckInVerifiedVisibility.PUBLIC;
  }
);
//Change Me Selectors
export const isMeUpdating = createSelector(iamState, ({ meUpdating }) => meUpdating);

export const isOrganizationUpdating = createSelector(
  iamState,
  ({ organizationUpdating }) => organizationUpdating
);
export const getGeocodeOrgLimit = createSelector(
  iamState,
  ({ geocodingOrgLimit }) => geocodingOrgLimit
);
export const isGeocodeOrgLimitReached = createSelector(
  iamState,
  ({ geocodingOrgLimitReached }) => geocodingOrgLimitReached
);

export const isGeocodeMmcLimitReached = createSelector(
  iamState,
  ({ geocodingMmcLimitReached }) => geocodingMmcLimitReached
);
export const getOrganizationSize = createSelector(
  iamState,
  ({ organizationSize }) => organizationSize
);
export const getOrganizationCumulSupport = createSelector(
  getOrganizationAddOnServices,
  (addOnServices) => addOnServices?.cumul
);
export const doesOrganizationSupportCumul = createSelector(
  getOrganizationCumulSupport,
  (cumul) =>
    (cumul === CumulAddOnServiceVariation.SKELETON_KEY_ONLY &&
      localSettings.wasSkeletonKeyUsed()) ||
    cumul === true
);

export const doesOrganizationSupportEmailService = createSelector(
  getOrganizationAddOnServices,
  (addOnServices) => !!addOnServices?.emailService
);

export const isEmailServiceSupported = createSelector(
  getMe,
  doesOrganizationSupportEmailService,
  getActivityTypes,
  (me, organizationSupport, activityTypes) =>
    !!me?.role.key &&
    ![RoleEnum.READ_ONLY, RoleEnum.READ_ONLY_WITH_EXPORTS].includes(me?.role.key) &&
    !!me?.addOns.emailService &&
    organizationSupport &&
    activityTypes.some(({ active, name }) => active && name === "Email")
);

export const isCumulEnabledForLoggedInUser = createSelector(getMe, (me) => me?.addOns.cumul);

export const isCumulEnabled = createSelector(
  doesOrganizationSupportCumul,
  isCumulEnabledForLoggedInUser,
  (orgSupportsCumul, cumulEnabled) => cumulEnabled && orgSupportsCumul
);

export const getCumulRole = createSelector(getMe, (me) => getUserCumulRole(me));

export const getLastUsedLayout = createSelector(
  iamState,
  ({ me }) => me?.metaData?.lastUsedLayout ?? {}
);
export const getOrganizationOwnerIds = createSelector(
  getUsers,
  getOrganizationOwnerId,
  (users: User[], ownerId?: User["id"]) => [
    ...users.filter(isOwnerPredicate).map(({ id }) => id),
    ...(ownerId ? [ownerId] : []),
  ]
);

export const doesUserHaveAccessToDupeCheck = createSelector(
  getRole,
  doesOrganizationSupportDupeCheck,
  getOrganizationSettings,
  hasPermission,
  (
    role: Role | undefined,
    dupeCheckSupported: boolean,
    orgSettings: Setting[] | undefined,
    hasPermission
  ) => {
    if (!dupeCheckSupported) {
      return false;
    }

    if (!hasPermission(PermissionName.DUPLICATES_READ)) {
      return false;
    }

    if (role?.key === RoleEnum.OWNER) {
      return true;
    }

    const accessSetting =
      orgSettings?.find(({ key }) => key === OrganizationSetting.DUPLICATE_MERGE_ACCESS)?.value ??
      DuplicateMergeAccessValue.OWNER;

    return role?.key === RoleEnum.MANAGER
      ? accessSetting !== DuplicateMergeAccessValue.OWNER
      : accessSetting === DuplicateMergeAccessValue.ALL_USERS;
  }
);

export const doesUserHaveAccessToImports = createSelector(
  getRole,
  getOrganizationSettings,
  hasPermission,
  (role: Role | undefined, orgSettings: Setting[] | undefined, hasPermission) => {
    const importRestriction =
      orgSettings?.find(({ key }) => key === OrganizationSetting.IMPORT_ALLOWED)?.value ??
      ImportRestrictionValue.OWNER;

    if (!hasPermission(PermissionName.IMPORT_READ)) {
      return false;
    }

    if (role?.key === RoleEnum.OWNER) {
      return true;
    }
    if (role?.key === RoleEnum.MANAGER) {
      return importRestriction !== ImportRestrictionValue.OWNER;
    }
    return importRestriction === ImportRestrictionValue.ALL_USERS;
  }
);

export const canCreateCustomMetrics = createSelector(
  getRole,
  getOrganizationSettings,
  (role: Role | undefined, orgSettings: Setting[] | undefined) => {
    // for non-managers, it's as easy as whether current user is owner or not
    if (role?.key !== RoleEnum.MANAGER) {
      return role?.key === RoleEnum.OWNER;
    }

    // to check if managers can create custom metrics, let's check respective org setting
    const customMetricsSetting =
      orgSettings?.find(({ key }) => key === OrganizationSetting.STACK_RANK_CUSTOM_METRICS)
        ?.value ?? CustomStackRankMetricsCreationSettingValue.OWNER;
    return customMetricsSetting === CustomStackRankMetricsCreationSettingValue.OWNER_MANAGER;
  }
);

export const getFlowStatuses = createSelector(
  getUserMetaData,
  ({ flowStatuses }) => flowStatuses ?? {}
);

export const getDismissedGuidesInfo = createSelector(
  getUserMetaData,
  ({ dismissedGuides }) => dismissedGuides ?? {}
);

export const getSandbox = createSelector(iamState, ({ orgSandbox }) => orgSandbox);

export const isOrgSandboxLoaded = createSelector(
  iamState,
  ({ orgSandboxLoaded }) => orgSandboxLoaded
);

export const getPlans = createSelector(iamState, ({ plans }) => plans);

export const getUsageLimits = createSelector(getMe, (me) => me?.usageLimits);

export const getOrgFavoriteSavedFilterIds = createSelector(
  getOrganizationMetaData,
  (metaData) => metaData.quickFilters ?? []
);

export const getOrganizationEmailBoilerplateText = createSelector(
  [(state) => getOrganizationSettingValue(state, OrganizationSetting.EMAIL_BOILERPLATE)],
  (emailSettings: EmailSettings | undefined) => {
    return emailSettings?.boilerplate ? (emailSettings.boilerplateText ?? "") : "";
  }
);

export const getBulkEmailSignatureText = createSelector(
  [(state) => getUserSettingValue(state, UserSetting.BULK_EMAIL)],
  (settingValue: BulkEmailSettingValue | undefined) => {
    return settingValue?.signature ? (settingValue.signatureText ?? "") : "";
  }
);
